import { VocabularyEntry } from '../state/search-vocabulary.store';
import { SearchFilterField } from '../models/search-filter-spec.model';
import {
  GO_TO_SUGGESTIONS, Suggestion, SuggestionGroup, UserRecordAggregate,
} from '../models/search-suggestions.model';

const DEFAULT_MAX_PER_GROUP = 5;

/** Top `max` keys by count, descending. Ties keep Map iteration order (insertion order) —
    deterministic given a deterministic aggregation pass, and good enough: this is a ranking
    heuristic, not a total order the app makes any correctness claim about. */
function topEntries<K>(counts: Map<K, number>, max: number): K[] {
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, max).map(([key]) => key);
}

/** Looks up each id against the vocabulary's entries for ONE field, by `String(value)` — the
    same coercion `VocabularyEntry.value`'s `string | number` type needs for a Map key. An id
    with no matching vocabulary entry (renamed/removed since the record was written) is silently
    skipped, not surfaced as a dead suggestion — the group may come back with fewer than
    `maxPerGroup` items; this is accepted, not backfilled from the next-ranked id. */
function suggestionsByIdField(
  ids: (string | number)[], field: SearchFilterField, vocabulary: VocabularyEntry[],
): Suggestion[] {
  const byValue = new Map(vocabulary.filter((e) => e.field === field).map((e) => [String(e.value), e]));
  const items: Suggestion[] = [];
  for (const id of ids) {
    const entry = byValue.get(String(id));
    if (entry) items.push({ query: entry.label, label: entry.label });
  }
  return items;
}

function suggestionsByStatusName(names: string[], vocabulary: VocabularyEntry[]): Suggestion[] {
  const byValue = new Map(vocabulary.filter((e) => e.field === 'status').map((e) => [String(e.value), e]));
  const items: Suggestion[] = [];
  for (const name of names) {
    const entry = byValue.get(name);
    if (entry) items.push({ query: entry.label, label: entry.label });
  }
  return items;
}

/** `normalizedNames` are ALREADY normalized (the aggregate's own contract — see
    `UserRecordAggregate.personNameCounts`'s doc), so this looks them up directly against each
    vocabulary `user` entry's OWN `normalizedText` (itself already normalized at index time —
    see `SearchVocabularyStore`'s `toEntries()`) rather than re-normalizing anything here. */
function suggestionsByPersonName(normalizedNames: string[], vocabulary: VocabularyEntry[]): Suggestion[] {
  const byNormalizedName = new Map(vocabulary.filter((e) => e.field === 'user').map((e) => [e.normalizedText, e]));
  const items: Suggestion[] = [];
  for (const name of normalizedNames) {
    const entry = byNormalizedName.get(name);
    if (entry) items.push({ query: entry.label, label: entry.label });
  }
  return items;
}

export function buildSuggestions(
  vocabulary: VocabularyEntry[],
  aggregate: UserRecordAggregate,
  maxPerGroup = DEFAULT_MAX_PER_GROUP,
): SuggestionGroup[] {
  return [
    { key: 'status', items: suggestionsByStatusName(topEntries(aggregate.statusCounts, maxPerGroup), vocabulary) },
    { key: 'location', items: suggestionsByIdField(topEntries(aggregate.locationCounts, maxPerGroup), 'entities', vocabulary) },
    { key: 'licenseType', items: suggestionsByIdField(topEntries(aggregate.licenseTypeCounts, maxPerGroup), 'licenseType', vocabulary) },
    { key: 'department', items: suggestionsByIdField(topEntries(aggregate.departmentCounts, maxPerGroup), 'department', vocabulary) },
    { key: 'people', items: suggestionsByPersonName(topEntries(aggregate.personNameCounts, maxPerGroup), vocabulary) },
    GO_TO_SUGGESTIONS,
  ];
}
